import React, { useState } from "react";
import "../../styles/ChatbotWidget.css"; // Adjust as needed

import fullscreenIcon from "../../assets/icons/fullscreen_icon.png";
import trashIcon from "../../assets/icons/trash_icon.png";
import downloadIcon from "../../assets/icons/download_icon.png";
import imageIcon from "../../assets/icons/image_icon2.png";
import aiAvatar from "../../assets/icons/ai_assistant.png";
import userAvatar from "../../assets/icons/user_avatar.png";
import ReactMarkdown from "react-markdown";

export default function ChatbotWidget({ clinicId }) {
    const [isThinking, setIsThinking] = useState(false);
    const [messages, setMessages] = useState([
      { sender: "ai", text: "Hello, this is Lisa. I am a professional aesthetic assistant. Please feel free to ask me anything!" }
    ]);
    const [input, setInput] = useState("");
    const [draft, setDraft] = useState("");
  
    // For grading & review
    const [ratings, setRatings] = useState({});
    const [hoverRatings, setHoverRatings] = useState({});
    const [reviews, setReviews] = useState({});
    const [retests, setRetests] = useState({});
    const [notes, setNotes] = useState({});
    const [showReviewer, setShowReviewer] = useState({});
    const [aiComments, setAiComments] = useState({});

  
    const handleSend = async () => {
        if (!draft.trim()) return;
    
        const userMsg = { sender: "user", text: draft };
        setMessages((prev) => [...prev, userMsg]);
        setInput(draft);
        setDraft("");
        setIsThinking(true);
    
        try {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/chat-basic`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ clinic_id: clinicId, query: draft }),
          });
    
          const data = await res.json();
          const aiMsg = { sender: "ai", text: data.answer };
          setMessages((prev) => [...prev, aiMsg]);
        } catch (err) {
          console.error("Error fetching AI response:", err);
        } finally {
          setIsThinking(false);
        }
      };
  
    const handleDeleteLastMessage = () => {
      setMessages((prev) => prev.slice(0, -1));
    };
  
    const handleToggleFullscreen = () => {
      const elem = document.documentElement;
      if (!document.fullscreenElement) {
        elem.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    };
  
    const handleDownloadChat = () => {
      const text = messages.map((m) => `${m.sender.toUpperCase()}: ${m.text}`).join("\n\n");
      const blob = new Blob([text], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "chat_history.txt";
      link.click();
    };
  
    return (
      <div className="chatbot-box">
        <div className="chat-header">
          <span className="chat-title">Olumis AI</span>
          <div className="chat-icons">
            <button title="Download Chat" onClick={handleDownloadChat}>
              <img src={downloadIcon} alt="Download" className="chat-icon" />
            </button>
            <button title="Full Screen" onClick={handleToggleFullscreen}>
              <img src={fullscreenIcon} alt="Full Screen" className="chat-icon" />
            </button>
            <button title="Clear Chat" onClick={handleDeleteLastMessage}>
              <img src={trashIcon} alt="Clear Chat" className="chat-icon" />
            </button>
          </div>
        </div>
  
        <div className="chat-messages">
          {messages.map((m, i) => (
            <div key={i}>
              <div className={`message-row ${m.sender}`}>
                {m.sender === "ai" && (
                  <>
                    <img src={aiAvatar} className="ai-avatar" alt="AI Avatar" />
                    <div className={`message ${m.sender}`}>
                      <ReactMarkdown>{m.text}</ReactMarkdown>
                    </div>
                  </>
                )}
  
                {m.sender === "user" && (
                  <>
                    <div className={`message ${m.sender}`}>
                      <ReactMarkdown>{m.text}</ReactMarkdown>
                    </div>
                    <img src={userAvatar} className="user-avatar" alt="User Avatar" />
                  </>
                )}
              </div>
  
              {/* Extra elements for AI messages */}
              {m.sender === "ai" && (
                <div className="ai-feedback-container">
                    {/* Rating Stars */}
                    <div className="rating-section">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <span
                        key={star}
                        className={`star ${
                            (hoverRatings[i] || ratings[i]) >= star ? "filled" : ""
                        }`}
                        onMouseEnter={() =>
                            setHoverRatings({ ...hoverRatings, [i]: star })
                        }
                        onMouseLeave={() =>
                            setHoverRatings({ ...hoverRatings, [i]: 0 })
                        }
                        onClick={() =>
                            setRatings({ ...ratings, [i]: star })
                        }
                        >
                        ⭐
                        </span>
                    ))}
                    </div>

                    {/* AI Reviewer Button (only if rating <= 3) */}
                    
                    {ratings[i] !== undefined && Number(ratings[i]) <= 3 && (
                    <div className="ai-reviewer-toggle">
                        <button
                        className="review-toggle-btn"
                        onClick={async () => {
                            const newShow = !showReviewer[i];
                            setShowReviewer({
                            ...showReviewer,
                            [i]: newShow,
                            });

                            // Fetch AI review comment only when expanding
                            if (newShow && !aiComments[i]) {
                            try {
                                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai-review`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ message: m.text }),
                                });
                                const data = await res.json();
                                setAiComments({
                                ...aiComments,
                                [i]: data.comment || "No review comment received.",
                                });
                            } catch (err) {
                                console.error("AI review fetch error:", err);
                                setAiComments({
                                ...aiComments,
                                [i]: "Error fetching AI review comment.",
                                });
                            }
                            }
                        }}
                        >
                        {showReviewer[i] ? "▲" : "▼"} AI Reviewer
                        </button>

                        {showReviewer[i] && (
                        <textarea
                            className="ai-review-textarea"
                            value={aiComments[i] || "Loading AI review..."}
                            readOnly
                        />
                        )}
                    </div>
                    )}



                    {/* Reviewer & Retest Section (only if toggle is open & rating <= 3) */}
                    {ratings[i] && ratings[i] <= 3 && showReviewer[i] && (
                    <>
                        {/* Reviewer Checkmarks */}
                        <div className="reviewer-section">
                        <button
                            className={reviews[i] === "approved" ? "selected" : ""}
                            onClick={() => {
                            setReviews({ ...reviews, [i]: "approved" });
                            }}
                        >
                            ✅ Approve
                        </button>
                        <button
                            className={reviews[i] === "rejected" ? "selected" : ""}
                            onClick={() => {
                            setReviews({ ...reviews, [i]: "rejected" });
                            }}
                        >
                            ❌ Reject
                        </button>
                        </div>

                        {/* If Approved → Show Re-Test button */}
                        {reviews[i] === "approved" && (
                        <div className="retest-section">
                            <button
                            className="retest-btn"
                            onClick={async () => {
                                try {
                                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/retest`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                    clinic_id: clinicId,
                                    original_message: m.text,
                                    ai_review_comment: aiComments[i] || "",
                                    human_notes: ""
                                    }),
                                });

                                const data = await res.json();
                                const updatedAiMsg = {
                                    sender: "ai",
                                    text: data.updated_answer || "No updated answer received."
                                };

                                setMessages((prev) => [...prev, updatedAiMsg]);
                                } catch (err) {
                                console.error("Error in Re-Test request:", err);
                                }
                            }}
                            >
                            🔄 Re-Test
                            </button>
                        </div>
                        )}

                        {/* If Rejected — Show Notes Section and Re-Test Button */}
                        {reviews[i] === "rejected" && (
                        <>
                            {/* Notes Section */}
                            <div className="notes-section">
                            <textarea
                                placeholder="After Re-Test Notes..."
                                value={notes[i] || ""}
                                onChange={(e) =>
                                setNotes({ ...notes, [i]: e.target.value })
                                }
                            />
                            </div>

                            {/* Submit Note Button */}
                            <div className="submit-note-section">
                            <button
                                className="submit-note-btn"
                                onClick={async () => {
                                try {
                                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/review/submit-note`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                        clinic_id: clinicId,
                                        message: m.text,
                                        note: notes[i] || ""
                                    }),
                                    });

                                    const data = await res.json();
                                    console.log("Note submitted:", data);
                                    alert("Note submitted successfully!");
                                } catch (err) {
                                    console.error("Error submitting note:", err);
                                    alert("Error submitting note");
                                }
                                }}
                            >
                                📩 Submit Note
                            </button>
                            </div>

                            {/* Re-Test Button */}
                            <div className="retest-section">
                            <button
                                className="retest-btn"
                                onClick={async () => {
                                const reviewText = aiComments[i] || "";
                                const humanNotes = notes[i] || "";

                                try {
                                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/retest`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                        clinic_id: clinicId,
                                        original_message: m.text,
                                        ai_review_comment: reviewText,
                                        human_notes: humanNotes
                                    }),
                                    });

                                    const data = await res.json();
                                    const updatedAiMsg = {
                                    sender: "ai",
                                    text: data.updated_answer || "No updated answer received."
                                    };

                                    setMessages((prev) => [...prev, updatedAiMsg]);
                                } catch (err) {
                                    console.error("Error in Re-Test request:", err);
                                }
                                }}
                            >
                                🔄 Re-Test
                            </button>
                            </div>
                        </>
                        )}

                    </>
                    )}
                </div>
                )}
            </div>
          ))}
  
          {isThinking && (
            <div className="message-row ai">
              <img src={aiAvatar} className="ai-avatar" alt="AI Avatar" />
              <div className="message ai typing-indicator">
                <span>Typing</span>
                <span className="dot dot1">.</span>
                <span className="dot dot2">.</span>
                <span className="dot dot3">.</span>
              </div>
            </div>
          )}
        </div>
  
        <div className="chat-input">
          <input
            type="file"
            accept="image/*"
            id="imageUpload"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const url = URL.createObjectURL(file);
                setMessages((prev) => [
                  ...prev,
                  { sender: "user", text: "[Image uploaded]", image: url }
                ]);
              }
            }}
          />
          <button
            title="Upload Image"
            className="image-upload-btn"
            onClick={() => document.getElementById("imageUpload").click()}
          >
            <img src={imageIcon} alt="Upload" className="chat-icon2" />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message here"
          />
          <button onClick={handleSend}>➤</button>
        </div>
  
        <div className="chat-footer">Powered by Olumis AI</div>
      </div>
    );
  }